import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Check, Play, Film, Mic, Music } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

import heroImage2 from "@/assets/hero-wedding-2.jpg";

interface Package {
  id: string;
  name: string;
  price: number;
  features: string[];
  is_featured: boolean;
  description: string | null;
}

interface Addon {
  id: string;
  name: string;
  price: number;
  description: string | null;
}

const Cinematography = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [loading, setLoading] = useState(true);
  const [heroImage, setHeroImage] = useState({ url: heroImage2, alt: "Wedding first dance" });

  useEffect(() => {
    const fetchData = async () => {
      const [packagesRes, addonsRes] = await Promise.all([
        supabase
          .from("pricing_packages")
          .select("*")
          .eq("is_active", true)
          .eq("category", "cinematography")
          .order("sort_order"),
        supabase
          .from("pricing_addons")
          .select("*")
          .eq("is_active", true)
          .eq("category", "cinematography")
          .order("sort_order"),
      ]);

      if (packagesRes.data) {
        const sorted = [...packagesRes.data].sort((a, b) => b.price - a.price);
        const featuredIdx = sorted.findIndex(p => p.is_featured);
        if (featuredIdx !== -1 && featuredIdx !== 1 && sorted.length > 1) {
          const [featured] = sorted.splice(featuredIdx, 1);
          sorted.splice(1, 0, featured);
        }
        setPackages(sorted);
      }
      if (addonsRes.data) setAddons(addonsRes.data);

      try {
        const { data: locations } = await supabase
          .from("portfolio_media_locations")
          .select("media_id")
          .eq("location", "cinematography_page");

        if (locations && locations.length > 0) {
          const mediaIds = locations.map((l) => l.media_id);
          const { data: mediaData } = await supabase
            .from("portfolio_media")
            .select("*")
            .in("id", mediaIds)
            .eq("is_active", true)
            .eq("media_type", "image")
            .order("sort_order", { ascending: true })
            .limit(1);

          if (mediaData && mediaData.length > 0) {
            setHeroImage({
              url: mediaData[0].url,
              alt: mediaData[0].alt_text || mediaData[0].title,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching portfolio media:", error);
      }

      setLoading(false);
    };
    fetchData();
  }, []);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(price);

  const valueProps = [
    {
      icon: Film,
      title: "Wedding Trailer",
      description:
        "A 4-6 minute cinematic edit set to music, capturing the best moments of your day in a shareable format.",
    },
    {
      icon: Mic,
      title: "Ceremony Edit",
      description:
        "The full ceremony from start to finish, professionally edited with multiple camera angles.",
    },
    {
      icon: Music,
      title: "Audio Excellence",
      description:
        "Crystal-clear vow recordings using professional wireless microphones and audio equipment.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Wedding Cinematography & Video | Pixie Clicks | NJ, FL & CO</title>
        <meta
          name="description"
          content="Cinematic wedding films that tell your love story. Professional videography packages starting at $995. Serving New Jersey, Florida & Colorado."
        />
        <meta property="og:title" content="Wedding Cinematography & Video | Pixie Clicks" />
        <meta property="og:description" content="Cinematic wedding films that tell your love story. Professional videography packages starting at $995." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pixieclicks.com/cinematography" />
        <link rel="canonical" href="https://pixieclicks.com/cinematography" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Wedding Cinematography",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Pixie Clicks",
              "areaServed": ["New Jersey", "Florida", "Colorado"]
            },
            "description": "Cinematic wedding films that tell your unique love story.",
            "offers": {
              "@type": "Offer",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "priceCurrency": "USD",
                "price": "995",
                "minPrice": "995"
              }
            }
          })}
        </script>
      </Helmet>
      <Layout>
        {/* Hero */}
        <section className="relative py-20 md:py-32 bg-muted/30">
          <div className="container-wide">
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <h1 className="font-serif text-4xl font-semibold leading-tight text-foreground md:text-5xl">
                  Cinematic Wedding Films{" "}
                  <span className="text-primary">That Tell Your Story</span>
                </h1>
                <p className="mt-6 text-lg text-muted-foreground">
                  Photos capture moments, but video captures motion, emotion,
                  and sound. Our cinematic wedding films let you relive the
                  laughter, the vows, the first dance — exactly as they
                  happened.
                </p>
                <ul className="mt-6 space-y-3">
                  {[
                    "4-6 min wedding trailer",
                    "Full ceremony edit available",
                    "Drone footage available",
                    "Licensed music",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <Check className="h-5 w-5 text-primary" />
                      <span className="text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
                <Button variant="hero" size="lg" className="mt-8" asChild>
                  <Link to="/contact">Book Your Videographer</Link>
                </Button>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="relative"
              >
                <img
                  src={heroImage.url}
                  alt={heroImage.alt}
                  className="rounded-2xl shadow-elevated"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <button className="flex h-20 w-20 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-rose transition-transform hover:scale-110">
                    <Play className="ml-1 h-8 w-8" />
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="section-padding">
          <div className="container-wide">
            <h2 className="text-center font-serif text-3xl font-semibold text-foreground md:text-4xl">
              What's Included
            </h2>
            <div className="mt-12 grid gap-8 md:grid-cols-3">
              {valueProps.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <item.icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl font-semibold text-foreground">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Packages */}
        <section className="section-padding bg-muted/30">
          <div className="container-wide">
            <h2 className="text-center font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Cinematography Packages
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-muted-foreground">
              Choose the package that fits your wedding day needs
            </p>

            {loading ? (
              <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="h-80 animate-pulse rounded-xl bg-muted"
                  />
                ))}
              </div>
            ) : (
              <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {packages.map((pkg, index) => (
                  <motion.div
                    key={pkg.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className={`relative flex flex-col rounded-xl border p-6 ${
                      pkg.is_featured
                        ? "border-primary bg-primary/5 shadow-rose"
                        : "border-border bg-card"
                    }`}
                  >
                    {pkg.is_featured && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                        Most Popular
                      </span>
                    )}
                    <h3 className="font-serif text-xl font-semibold text-foreground">
                      {pkg.name.replace("Cinematography ", "")}
                    </h3>
                    <p className="mt-2 text-3xl font-bold text-primary">
                      {formatPrice(pkg.price)}
                    </p>
                    {pkg.description && (
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                        {pkg.description}
                      </p>
                    )}
                    <ul className="mt-6 flex-1 space-y-2">
                      {pkg.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start gap-2 text-sm"
                        >
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                          <span className="text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant={pkg.is_featured ? "hero" : "outline"}
                      className="mt-6 w-full"
                      asChild
                    >
                      <Link to={`/contact?package=${encodeURIComponent(pkg.name)}`}>Check Availability</Link>
                    </Button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Add-ons */}
        {addons.length > 0 && (
          <section className="section-padding">
            <div className="container-wide">
              <h2 className="text-center font-serif text-3xl font-semibold text-foreground md:text-4xl">
                Make It Yours
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-center text-muted-foreground">
                Enhance your film with these popular add-ons
              </p>
              <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {addons.map((addon) => (
                  <div
                    key={addon.id}
                    className="flex flex-col rounded-xl border border-border bg-card p-5 hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-medium text-foreground">{addon.name}</h3>
                      <p className="text-xl font-bold text-primary">
                        {formatPrice(addon.price)}
                      </p>
                    </div>
                    {addon.description && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        {addon.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="section-padding bg-muted/30">
          <div className="container-narrow text-center">
            <h2 className="font-serif text-3xl font-semibold text-foreground md:text-4xl">
              Ready to Capture Your Story?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Contact us to check availability and book your cinematographer.
            </p>
            <Button variant="hero" size="lg" className="mt-8" asChild>
              <Link to="/contact">Get in Touch</Link>
            </Button>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default Cinematography;
